import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, RefreshCw, Wand2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { customFieldsApi } from '../services/customFieldsApi';
import logger from '../utils/logger';

const FIELDTYPE_OPTIONS = [
  { value: 'text', label: 'Text (single line)' },
  { value: 'textarea', label: 'Text (multi-line)' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Checkbox (yes/no)' },
  { value: 'select', label: 'Select (dropdown)' },
];

const emptyForm = {
  doctype: '',
  fieldname: '',
  label: '',
  fieldtype: 'text',
  required: false,
  options: '',       // comma-separated in the form; split before submit
  default: '',
  help_text: '',
  active: true,
  sort_order: 0,
  in_list_view: false,
};

const CustomFieldManager = () => {
  const [doctypes, setDoctypes] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDoctype, setFilterDoctype] = useState('all');
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const loadDoctypes = useCallback(async () => {
    try {
      const r = await customFieldsApi.listDoctypes();
      setDoctypes(r.data || []);
      if (r.data?.length) {
        setFormData((f) => (f.doctype ? f : { ...f, doctype: r.data[0] }));
      }
    } catch (err) {
      logger.error('Failed to load doctypes', err);
    }
  }, []);

  const loadFields = useCallback(async () => {
    setLoading(true);
    try {
      const r = await customFieldsApi.list({
        ...(filterDoctype !== 'all' ? { doctype: filterDoctype } : {}),
      });
      setFields(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  }, [filterDoctype]);

  useEffect(() => { loadDoctypes(); }, [loadDoctypes]);
  useEffect(() => { loadFields(); }, [loadFields]);

  const filtered = useMemo(() => {
    if (!search) return fields;
    const q = search.toLowerCase();
    return fields.filter((f) =>
      f.fieldname.toLowerCase().includes(q) ||
      f.label.toLowerCase().includes(q) ||
      f.doctype.toLowerCase().includes(q)
    );
  }, [fields, search]);

  const openCreate = () => {
    setFormData({
      ...emptyForm,
      doctype: filterDoctype !== 'all' ? filterDoctype : (doctypes[0] || ''),
    });
    setEditMode(false);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setFormData({
      doctype: row.doctype,
      fieldname: row.fieldname,
      label: row.label,
      fieldtype: row.fieldtype,
      required: !!row.required,
      options: (row.options || []).join(', '),
      default: row.default ?? '',
      help_text: row.help_text ?? '',
      active: !!row.active,
      sort_order: row.sort_order ?? 0,
      in_list_view: !!row.in_list_view,
    });
    setEditMode(row);
    setDialogOpen(true);
  };

  const buildPayload = () => {
    const payload = { ...formData };
    payload.options = formData.fieldtype === 'select'
      ? formData.options.split(',').map((x) => x.trim()).filter(Boolean)
      : null;
    if (payload.default === '') payload.default = null;
    if (payload.help_text === '') payload.help_text = null;
    payload.sort_order = Number(formData.sort_order) || 0;
    return payload;
  };

  const handleSave = async () => {
    if (!formData.doctype || !formData.fieldname || !formData.label) {
      toast.error('DocType, Fieldname and Label are required');
      return;
    }
    if (formData.fieldtype === 'select' && !formData.options.trim()) {
      toast.error('Select fields need at least one option (comma-separated)');
      return;
    }
    try {
      const payload = buildPayload();
      if (editMode) {
        // doctype + fieldname are immutable on the backend
        const { doctype, fieldname, ...rest } = payload;
        await customFieldsApi.update(editMode.field_id, rest);
        toast.success('Custom field updated');
      } else {
        await customFieldsApi.create(payload);
        toast.success('Custom field created');
      }
      setDialogOpen(false);
      loadFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(
      `Delete custom field '${row.fieldname}' from doctype '${row.doctype}'?\n` +
      `Existing records that stored values for this field will keep the data ` +
      `but it will no longer render on forms.`
    )) return;
    try {
      await customFieldsApi.delete(row.field_id);
      toast.success('Deleted');
      loadFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="custom-field-manager-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Wand2 className="w-7 h-7 text-orange-600" />
              Custom Field Manager
            </h1>
            <p className="text-gray-600 mt-1">
              Add ad-hoc fields to master data &amp; transactional DocTypes without
              a code change. Values are persisted in each record&apos;s
              <code className="mx-1 px-1 bg-gray-100 rounded text-xs">custom_data</code>
              sub-document.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadFields} data-testid="cf-refresh">
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
            <Button onClick={openCreate} data-testid="cf-new-btn">
              <Plus className="w-4 h-4 mr-1" />New Custom Field
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="pt-4 grid grid-cols-3 gap-3">
            <div className="relative col-span-2">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                placeholder="Search fieldname, label, doctype…"
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="cf-search-input"
              />
            </div>
            <Select value={filterDoctype} onValueChange={setFilterDoctype}>
              <SelectTrigger data-testid="cf-filter-doctype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All DocTypes</SelectItem>
                {doctypes.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{filtered.length} Field(s)</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                No custom fields yet. Click <strong>New Custom Field</strong> to start.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>DocType</TableHead>
                    <TableHead>Fieldname</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.field_id} data-testid={`cf-row-${row.fieldname}`}>
                      <TableCell><Badge variant="outline">{row.doctype}</Badge></TableCell>
                      <TableCell className="font-mono text-sm">{row.fieldname}</TableCell>
                      <TableCell>{row.label}</TableCell>
                      <TableCell><Badge>{row.fieldtype}</Badge></TableCell>
                      <TableCell>{row.required ? '✓' : '—'}</TableCell>
                      <TableCell>
                        <Badge variant={row.active ? 'default' : 'secondary'}>
                          {row.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{row.sort_order}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)} data-testid={`cf-edit-${row.fieldname}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(row)} data-testid={`cf-delete-${row.fieldname}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="cf-dialog">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit' : 'New'} Custom Field</DialogTitle>
              <DialogDescription>
                Define a new user-defined field on the selected DocType. Values entered by users
                will be stored in each record&apos;s <code>custom_data</code> sub-document.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>DocType *</Label>
                <Select
                  value={formData.doctype}
                  onValueChange={(v) => setFormData({ ...formData, doctype: v })}
                  disabled={!!editMode}
                >
                  <SelectTrigger data-testid="cf-form-doctype"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {doctypes.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fieldname * <span className="text-xs text-gray-500">(snake_case)</span></Label>
                <Input
                  value={formData.fieldname}
                  disabled={!!editMode}
                  maxLength={60}
                  placeholder="e.g. preferred_currency"
                  onChange={(e) => setFormData({ ...formData, fieldname: e.target.value.toLowerCase() })}
                  data-testid="cf-form-fieldname"
                />
              </div>
              <div className="col-span-2">
                <Label>Label *</Label>
                <Input
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  data-testid="cf-form-label"
                />
              </div>
              <div>
                <Label>Field Type *</Label>
                <Select
                  value={formData.fieldtype}
                  onValueChange={(v) => setFormData({ ...formData, fieldtype: v })}
                >
                  <SelectTrigger data-testid="cf-form-fieldtype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FIELDTYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                />
              </div>
              {formData.fieldtype === 'select' && (
                <div className="col-span-2">
                  <Label>Options * <span className="text-xs text-gray-500">(comma-separated)</span></Label>
                  <Input
                    value={formData.options}
                    placeholder="e.g. LOW, MEDIUM, HIGH"
                    onChange={(e) => setFormData({ ...formData, options: e.target.value })}
                    data-testid="cf-form-options"
                  />
                </div>
              )}
              <div>
                <Label>Default Value</Label>
                <Input
                  value={formData.default ?? ''}
                  onChange={(e) => setFormData({ ...formData, default: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.required}
                    onCheckedChange={(v) => setFormData({ ...formData, required: !!v })}
                    data-testid="cf-form-required"
                  />
                  <Label>Required</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.active}
                    onCheckedChange={(v) => setFormData({ ...formData, active: !!v })}
                    data-testid="cf-form-active"
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.in_list_view}
                    onCheckedChange={(v) => setFormData({ ...formData, in_list_view: !!v })}
                    data-testid="cf-form-in-list-view"
                  />
                  <Label>Show in List View</Label>
                </div>
              </div>
              <div className="col-span-2">
                <Label>Help Text</Label>
                <Textarea
                  rows={2}
                  value={formData.help_text ?? ''}
                  onChange={(e) => setFormData({ ...formData, help_text: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="cf-save-btn">
                {editMode ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CustomFieldManager;
